import type { ReactNode } from "react";

import type { UsageDriver, UsageProviderKind } from "@cmmd-forge/contracts";
import type {
  DriverTotals,
  EnvironmentTotals,
  KindTotals,
  ModelTotals,
  ProviderTotals,
} from "@cmmd-forge/shared/usageMerge";
import {
  formatCount,
  formatPercent,
  formatTokens,
  formatUsd,
} from "@cmmd-forge/shared/usageFormat";

import { ProviderMark } from "./UsageSummaryPanel";
import { usageCoverageLabel, type UsageCoverageCounts } from "@cmmd-forge/shared/usageCoverageCopy";
import { KIND_LABELS } from "./usageKindRows";
import { PROVIDER_PRESENTATION } from "./usageProviders";

/**
 * Who priced an environment's cost, as a short cell.
 *
 * CMMD prices the ledger rows it holds; everything an environment reported on
 * its own (Recorded turns not yet acknowledged, CLI transcripts, Forge agents
 * outside the ledger) is estimated by this client from the rate table.
 */
function costProvenanceLabel(lanes: readonly EnvironmentTotals["lanes"][number][]): string {
  const cmmd = lanes.includes("cmmd");
  const local = lanes.some((lane) => lane !== "cmmd");
  if (cmmd && local) return "CMMD and this machine";
  return cmmd ? "Priced by CMMD" : "Estimated on this machine";
}

/**
 * What the page calls each driver. `other` is a launcher string this build
 * does not know; naming it keeps a renamed harness visible.
 */
const DRIVER_LABELS: Record<UsageDriver, string> = {
  forge: "Forge",
  forge_unrecorded: "Forge (not recorded here)",
  t3code: "t3code",
  codex_desktop: "Codex Desktop",
  claude_desktop: "Claude Desktop",
  cli: "CLI",
  sdk: "Agent SDK",
  other: "Other",
};

const HEAD_CLASS = "border-b border-border text-left text-xs text-muted-foreground";
const ROW_CLASS = "border-b border-border/50 transition-colors hover:bg-muted/50";
const CELL_MUTED = "py-2 text-right text-muted-foreground tabular-nums";
const CELL = "py-2 text-right text-foreground tabular-nums";

/**
 * The frame every breakdown table shares: a fixed layout that scrolls inside
 * its own box, a label column, and right-aligned figures. The first header
 * names the row; the rest are numbers.
 */
function BreakdownTable({
  testId,
  minWidthClass,
  widths,
  headers,
  rows,
}: {
  readonly testId: string;
  readonly minWidthClass: string;
  readonly widths: readonly string[];
  readonly headers: readonly string[];
  readonly rows: readonly ReactNode[];
}) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={`w-full ${minWidthClass} table-fixed text-sm`} data-testid={testId}>
        <colgroup>
          {widths.map((width, index) => (
            <col key={headers[index]} className={width} />
          ))}
        </colgroup>
        <thead>
          <tr className={HEAD_CLASS}>
            {headers.map((header, index) => (
              <th
                key={header}
                className={index === 0 ? "py-2 font-normal" : "py-2 text-right font-normal"}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="py-6 text-center text-muted-foreground">
                No activity in this window.
              </td>
            </tr>
          ) : (
            rows
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Cost, its share, and tokens: the three figures every row carries. */
function MoneyCells({
  costUsd,
  costShare,
  totalTokens,
}: {
  readonly costUsd: number;
  readonly costShare: number;
  readonly totalTokens: number;
}) {
  return (
    <>
      <td className={CELL}>{formatUsd(costUsd)}</td>
      <td className={CELL_MUTED}>{formatPercent(costShare)}</td>
      <td className={CELL_MUTED}>{formatTokens(totalTokens)}</td>
    </>
  );
}

const FIVE_EVEN_COLUMNS = ["w-2/6", "w-1/6", "w-1/6", "w-1/6", "w-1/6"] as const;

/**
 * How much of a row nobody could price, as one short text cell.
 *
 * Empty when every record on the row is priced, which is most rows. A row can
 * be mixed, so this counts rather than labels: gpt-6-astra is two priced
 * records and one unpriced, and no single word is true of it.
 */
function CoverageCell({ row }: { readonly row: UsageCoverageCounts }) {
  return <td className={CELL_MUTED}>{usageCoverageLabel(row)}</td>;
}

/** One row per environment the merge could name, most expensive first. */
export function UsageEnvironmentTable({
  environments,
  labelFor,
}: {
  readonly environments: readonly EnvironmentTotals[];
  readonly labelFor: (row: EnvironmentTotals) => string;
}) {
  return (
    <BreakdownTable
      testId="usage-environment-table"
      minWidthClass="min-w-[36rem]"
      widths={["w-[24%]", "w-[13%]", "w-[10%]", "w-[13%]", "w-[22%]", "w-[18%]"]}
      headers={["Environment", "Cost", "Share", "Tokens", "Priced by", "Coverage"]}
      rows={environments.map((row) => (
        <tr key={row.key} className={ROW_CLASS}>
          <td className="py-2 text-foreground">
            <span className="block truncate">{labelFor(row)}</span>
          </td>
          <MoneyCells
            costUsd={row.costUsd}
            costShare={row.costShare}
            totalTokens={row.totalTokens}
          />
          <td className={CELL_MUTED}>{costProvenanceLabel(row.lanes)}</td>
          <CoverageCell row={row} />
        </tr>
      ))}
    />
  );
}

/**
 * One row per model, in whatever order the caller ranked them.
 *
 * The coverage cell is the last column so the three figures stay adjacent; it
 * is text rather than a number, and mixing it in would break the run of
 * right-aligned money.
 */
export function UsageModelTable({ models }: { readonly models: readonly ModelTotals[] }) {
  return (
    <BreakdownTable
      testId="usage-model-table"
      // A long model name plus three numeric columns and the coverage cell
      // overruns 320px, so the table scrolls inside its own box rather than
      // pushing the page sideways.
      minWidthClass="min-w-[28rem]"
      widths={["w-[32%]", "w-[16%]", "w-[13%]", "w-[16%]", "w-[23%]"]}
      headers={["Model", "Cost", "Share", "Tokens", "Coverage"]}
      rows={models.map((row) => (
        <tr key={`${row.provider}:${row.model}`} className={ROW_CLASS}>
          <td className="py-2 text-foreground">
            <span className="flex items-center gap-2">
              <ProviderMark provider={row.provider} className="size-3.5" />
              <span className="truncate">{row.model}</span>
            </span>
          </td>
          <MoneyCells
            costUsd={row.costUsd}
            costShare={row.costShare}
            totalTokens={row.totalTokens}
          />
          <CoverageCell row={row} />
        </tr>
      ))}
    />
  );
}

/** One row per harness, in the merge's cost order. */
export function UsageProviderTable({
  providers,
  activeProviders,
}: {
  readonly providers: readonly ProviderTotals[];
  readonly activeProviders: readonly UsageProviderKind[];
}) {
  const rows = activeProviders.flatMap((provider) => {
    const totals = providers.find((entry) => entry.provider === provider);
    return totals === undefined ? [] : [totals];
  });
  return (
    <BreakdownTable
      testId="usage-provider-table"
      minWidthClass="min-w-[26rem]"
      widths={FIVE_EVEN_COLUMNS}
      headers={["Provider", "Cost", "Share", "Tokens", "Sessions"]}
      rows={rows.map((row) => (
        <tr key={row.provider} className={ROW_CLASS}>
          <td className="py-2 text-foreground">
            <span className="flex items-center gap-2">
              <ProviderMark provider={row.provider} className="size-3.5" />
              <span className="truncate">{PROVIDER_PRESENTATION[row.provider].label}</span>
            </span>
          </td>
          <MoneyCells
            costUsd={row.costUsd}
            costShare={row.costShare}
            totalTokens={row.totalTokens}
          />
          <td className={CELL_MUTED}>{formatCount(row.sessions)}</td>
        </tr>
      ))}
    />
  );
}

/**
 * The Local-only lane by the harness that drove each session. Share is of
 * that lane, not of the page: this table explains the CLI work outside Forge.
 */
export function UsageDriverTable({ drivers }: { readonly drivers: readonly DriverTotals[] }) {
  return (
    <BreakdownTable
      testId="usage-driver-table"
      minWidthClass="min-w-[26rem]"
      widths={FIVE_EVEN_COLUMNS}
      headers={["Driver", "Cost", "Share of Local-only", "Tokens", "Records"]}
      rows={drivers.map((row) => (
        <tr key={row.driver} className={ROW_CLASS}>
          <td className="py-2 text-foreground">
            <span className="block truncate">{DRIVER_LABELS[row.driver]}</span>
          </td>
          <MoneyCells
            costUsd={row.costUsd}
            costShare={row.costShare}
            totalTokens={row.totalTokens}
          />
          <td className={CELL_MUTED}>{formatCount(row.records)}</td>
        </tr>
      ))}
    />
  );
}

/**
 * What the total is made of: a turn's own work, the subagents inside it, and
 * the one-shot calls Forge made on its own behalf. Share is of the page total,
 * the way the model rows read.
 */
export function UsageKindTable({ kinds }: { readonly kinds: readonly KindTotals[] }) {
  return (
    <BreakdownTable
      testId="usage-kind-table"
      minWidthClass="min-w-[26rem]"
      widths={FIVE_EVEN_COLUMNS}
      headers={["Kind", "Cost", "Share", "Tokens", "Turn count"]}
      rows={kinds.map((row) => (
        <tr key={row.kind} className={ROW_CLASS}>
          <td className="py-2 text-foreground">
            <span className="block truncate">{KIND_LABELS[row.kind]}</span>
          </td>
          <MoneyCells
            costUsd={row.costUsd}
            costShare={row.costShare}
            totalTokens={row.totalTokens}
          />
          <td className={CELL_MUTED}>
            {row.turns === null ? (
              // Empty rather than a dash: a dash reads as zero turns, and the
              // difference between none and nobody could count them is the
              // reason the field is nullable at all.
              <span className="sr-only">Not counted</span>
            ) : (
              formatCount(row.turns)
            )}
          </td>
        </tr>
      ))}
    />
  );
}
